import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Tab,
    Tabs,
    TextField,
} from '@mui/material'
import { testApiSlice } from '../../../../../../features/section/testSlice'
import { tone, ui } from '../../../../../../utils/uiTokens'
import QuestionCard from './QuestionCard'
import { applyAnswer, imageToDataUri, nextKey, solveAnswers, streamParse } from './importApi'
import { parseText } from './parseText'
import { emptyAnswer, ImportQuestion, issueOf } from './types'

interface Props {
    test: any
    onClose: () => void
    onSaved: () => void
}

type Filter = 'all' | 'single' | 'multi' | 'match' | 'image' | 'warn'

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const TYPE = { single: 'ONE_ANSWER', multi: 'MULTI_SELECT', match: 'MATCHING' }

// Вопрос из ответа сервера -> тело запроса: сохранение заменяет весь список теста, поэтому старые идут с id
const existingToRequest = (q: any) => {
    const answers = [...(q.answers || [])].sort((a, b) => a.position - b.position)
    return {
        id: q.id,
        content: q.content,
        questionTypeEnum: q.questionTypeEnum,
        videoLink: q.videoLink || '',
        position: q.position,
        answers: answers.map((a) => ({ id: a.id, content: a.content, position: a.position, correct: a.correct })),
        complianceItems:
            q.questionTypeEnum === 'MATCHING'
                ? (q.complianceItems || []).map((i) => ({
                      id: i.id,
                      content: i.content,
                      position: i.position,
                      correctAnswerPositions: (i.correctAnswerIds || [])
                          .map((id) => answers.findIndex((a) => a.id === id) + 1)
                          .filter((p) => p > 0),
                  }))
                : [],
    }
}

const newToRequest = (q: ImportQuestion, content: string) => {
    const match = q.type === 'match'
    const right = q.correct as string[]
    const pair = q.correct as Record<string, string>
    return {
        content,
        questionTypeEnum: TYPE[q.type],
        videoLink: '',
        position: 0,
        answers: q.opts.map((o, i) => ({ content: esc(o[1]), position: i + 1, correct: !match && right.includes(o[0]) })),
        complianceItems: match
            ? q.subs!.map((s, i) => ({
                  content: esc(s[1]),
                  position: i + 1,
                  correctAnswerPositions: [q.opts.findIndex((o) => o[0] === pair[s[0]]) + 1],
              }))
            : [],
    }
}

const ImportQuestionsDialog: React.FC<Props> = ({ test, onClose, onSaved }) => {
    const { formatMessage } = useIntl()
    const [getQuestions] = testApiSlice.useLazyGetTestQuestionsQuery()
    const [createQuestions] = testApiSlice.useCreateTestQuestionsMutation()

    const [step, setStep] = useState<'upload' | 'review' | 'done'>('upload')
    const [tab, setTab] = useState<'text' | 'shots'>('text')
    const [text, setText] = useState('')
    const [shots, setShots] = useState<File[]>([])
    const [qs, setQs] = useState<ImportQuestion[]>([])
    const [filter, setFilter] = useState<Filter>('all')
    const [editKey, setEditKey] = useState<number | null>(null)
    const [busy, setBusy] = useState<'parse' | 'solve' | null>(null)
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState('')
    const [savedCount, setSavedCount] = useState(0)
    const ctl = useRef<AbortController | null>(null)
    const editRef = useRef<number | null>(null)
    const fileInput = useRef<HTMLInputElement>(null)
    editRef.current = editKey
    const previews = useMemo(() => shots.map((f) => URL.createObjectURL(f)), [shots])
    useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews])

    const abort = () => ctl.current?.abort()
    const reset = () => {
        abort()
        setStep('upload')
        setText('')
        setShots([])
        setQs([])
        setFilter('all')
        setEditKey(null)
        setBusy(null)
        setErr('')
    }
    const close = () => {
        reset()
        onClose()
    }

    // Ctrl+V со скриншотом: переключаемся на вкладку скриншотов, если текста в буфере нет
    useEffect(() => {
        if (!test || step !== 'upload') return undefined
        const onPaste = (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'))
            if (!files.length || e.clipboardData?.getData('text/plain')) return
            e.preventDefault()
            setTab('shots')
            setShots((s) => [...s, ...files])
        }
        document.addEventListener('paste', onPaste)
        return () => document.removeEventListener('paste', onPaste)
    }, [test, step])

    const addFiles = (list: FileList | File[] | null) => {
        const files = Array.from(list || []).filter((f) => /^image\/(png|jpeg|webp)$/.test(f.type))
        if (files.length) setShots((s) => [...s, ...files])
    }

    // Ответы ИИ ложатся только в вопросы, которые остались пустыми и не открыты в правке
    const solveMissing = async (list: ImportQuestion[], signal: AbortSignal) => {
        const need = list.filter((q) => emptyAnswer(q) && !q.hasImage).slice(0, 40)
        if (!need.length) return
        setBusy('solve')
        const res = await solveAnswers(need, signal)
        setQs((prev) =>
            prev.map((q) => {
                const r = res.find((x) => x.id === q.key)
                return r && r.answer.length && emptyAnswer(q) && q.key !== editRef.current ? applyAnswer(q, r.answer) : q
            }),
        )
    }

    const start = async () => {
        setErr('')
        abort()
        const c = new AbortController()
        ctl.current = c
        setQs([])
        setFilter('all')
        setStep('review')
        const got: ImportQuestion[] = []
        try {
            if (tab === 'text') {
                const ruled = parseText(text).map((q) => ({ ...q, key: nextKey() }))
                if (ruled.length) {
                    got.push(...ruled)
                    setQs(ruled)
                }
            }
            if (!got.length) {
                setBusy('parse')
                await streamParse(
                    tab === 'text' ? text : '',
                    tab === 'shots' ? shots : [],
                    (q) => {
                        got.push(q)
                        setQs((p) => [...p, q])
                    },
                    c.signal,
                )
            }
            if (!got.length) throw new Error('none')
            await solveMissing(got, c.signal)
        } catch (e: any) {
            if (c.signal.aborted) return
            setErr(formatMessage({ id: e?.message === 'none' ? 'qiNone' : 'qiError' }))
            if (!got.length) setStep('upload')
        } finally {
            if (!c.signal.aborted) setBusy(null)
        }
    }

    const save = async () => {
        setSaving(true)
        try {
            const existing = await getQuestions(test.id, false).unwrap()
            const urls = new Map<number, string>()
            const added = []
            for (const q of qs) {
                let content = `<p>${esc(q.text)}</p>`
                const file = q.hasImage && q.screenshot ? shots[q.screenshot - 1] : null
                if (file) {
                    if (!urls.has(q.screenshot!)) urls.set(q.screenshot!, await imageToDataUri(file))
                    content = `<p><img src="${urls.get(q.screenshot!)}" alt=""></p>${content}`
                }
                added.push(newToRequest(q, content))
            }
            const questions = [...(existing?.questions || []).map(existingToRequest), ...added]
            await createQuestions({ topicTestId: Number(test.id), questions } as any).unwrap()
            setSavedCount(added.length)
            setStep('done')
            onSaved()
        } catch (e) {
            toast.error(formatMessage({ id: 'saveFail' }))
        } finally {
            setSaving(false)
        }
    }

    const count = (f: Filter) =>
        f === 'all' ? qs.length
        : f === 'image' ? qs.filter((q) => q.hasImage).length
        : f === 'warn' ? qs.filter((q) => issueOf(q)).length
        : qs.filter((q) => q.type === f).length
    const shown = qs.filter((q) =>
        filter === 'all' ? true
        : filter === 'image' ? q.hasImage
        : filter === 'warn' ? !!issueOf(q)
        : q.type === filter,
    )
    const warns = count('warn')
    const canParse = tab === 'text' ? text.trim().length > 10 : shots.length > 0
    const canSave = !busy && editKey === null && warns === 0 && qs.length > 0 && !saving
    const stepN = step === 'upload' ? 1 : step === 'review' ? 2 : 3

    return (
        <Dialog open={!!test} onClose={close} fullWidth maxWidth="lg">
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ color: ui.ink, fontWeight: 700 }}>{formatMessage({ id: 'qiTitle' })}</Box>
                <Box sx={{ color: ui.sub, fontSize: 14, fontWeight: 400 }}>{test?.title}</Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                    {[1, 2, 3].map((n) => (
                        <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: n <= stepN ? ui.pink : ui.muted, fontSize: 14, fontWeight: 600 }}>
                            <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, color: n <= stepN ? '#fff' : ui.muted, background: n <= stepN ? ui.pink : ui.track }}>
                                {n}
                            </Box>
                            {formatMessage({ id: `qiStep${n}` })}
                        </Box>
                    ))}
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {step === 'upload' && (
                    <Box>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                            <Tab value="text" label={formatMessage({ id: 'qiTabText' })} />
                            <Tab value="shots" label={formatMessage({ id: 'qiTabShots' })} />
                        </Tabs>

                        {tab === 'text' ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2 }}>
                                <TextField
                                    multiline
                                    minRows={14}
                                    maxRows={22}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={formatMessage({ id: 'qiTextHint' })}
                                />
                                <Box sx={{ background: ui.surface, border: `1px solid ${ui.border}`, borderRadius: ui.radius, p: 2 }}>
                                    <Box sx={{ fontWeight: 600, color: ui.ink, mb: 1 }}>{formatMessage({ id: 'qiSamples' })}</Box>
                                    <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: ui.sub }}>
                                        {formatMessage({ id: 'qiSample' })}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <Box
                                    onClick={() => fileInput.current?.click()}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault()
                                        addFiles(e.dataTransfer.files)
                                    }}
                                    sx={{ border: `2px dashed ${ui.border}`, borderRadius: ui.radius, p: 4, textAlign: 'center', cursor: 'pointer', background: ui.surface }}
                                >
                                    <Box sx={{ color: ui.ink, fontWeight: 600 }}>{formatMessage({ id: 'qiDrop' })}</Box>
                                    <Box sx={{ color: ui.muted, fontSize: 13, mt: 0.5 }}>{formatMessage({ id: 'qiDropSub' })}</Box>
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        hidden
                                        multiple
                                        accept="image/png,image/jpeg,image/webp"
                                        onChange={(e) => {
                                            addFiles(e.target.files)
                                            e.target.value = ''
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                                    {shots.map((f, i) => (
                                        <Box key={`${f.name}-${i}`} sx={{ width: 140 }}>
                                            <Box component="img" src={previews[i]} alt="" sx={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: '8px', border: `1px solid ${ui.border}` }} />
                                            <Button size="small" onClick={() => setShots((s) => s.filter((_, j) => j !== i))}>
                                                {formatMessage({ id: 'qiRemove' })}
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {err && <Box sx={{ color: tone.bad.text, mt: 2 }}>{err}</Box>}
                    </Box>
                )}

                {step === 'review' && (
                    <Box>
                        {busy && <LinearProgress sx={{ mb: 1 }} />}
                        {busy && (
                            <Box sx={{ color: ui.sub, fontSize: 14, mb: 1.5 }}>
                                {busy === 'parse'
                                    ? formatMessage({ id: 'qiReading' }, { count: qs.length })
                                    : formatMessage({ id: 'qiSolving' })}
                            </Box>
                        )}
                        {err && <Box sx={{ color: tone.bad.text, mb: 1.5 }}>{err}</Box>}
                        {warns > 0 && !busy && (
                            <Box sx={{ background: tone.yellow.bg, border: `1px solid ${tone.yellow.border}`, color: tone.yellow.text, borderRadius: '8px', p: 1.5, mb: 2, fontSize: 14 }}>
                                {formatMessage({ id: 'qiWarnBar' }, { count: warns })}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            {(['all', 'single', 'multi', 'match', 'image', 'warn'] as Filter[]).map((f) => (
                                <Button
                                    key={f}
                                    size="small"
                                    variant={filter === f ? 'contained' : 'outlined'}
                                    onClick={() => setFilter(f)}
                                >
                                    {`${formatMessage({ id: `qiFilter${f[0].toUpperCase()}${f.slice(1)}` })} · ${count(f)}`}
                                </Button>
                            ))}
                        </Box>
                        {shown.map((q) => (
                            <QuestionCard
                                key={q.key}
                                q={q}
                                n={qs.indexOf(q) + 1}
                                editing={editKey === q.key}
                                locked={editKey !== null && editKey !== q.key}
                                onEdit={() => setEditKey(q.key)}
                                onChange={(next) => setQs((p) => p.map((x) => (x.key === q.key ? next : x)))}
                                onSave={(next) => {
                                    setQs((p) => p.map((x) => (x.key === q.key ? next : x)))
                                    setEditKey(null)
                                }}
                                onCancel={() => setEditKey(null)}
                            />
                        ))}
                    </Box>
                )}

                {step === 'done' && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Box sx={{ color: tone.ok.text, fontSize: 20, fontWeight: 700 }}>
                            {formatMessage({ id: 'qiDone' }, { count: savedCount })}
                        </Box>
                        <Box sx={{ color: ui.sub, mt: 1 }}>{formatMessage({ id: 'qiDoneSub' })}</Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {step === 'upload' && (
                    <>
                        <Button variant="outlined" onClick={close}>{formatMessage({ id: 'cancel' })}</Button>
                        <Button variant="contained" disabled={!canParse} onClick={start}>
                            {formatMessage({ id: 'qiParse' })}
                        </Button>
                    </>
                )}
                {step === 'review' && (
                    <>
                        {editKey !== null && (
                            <Box sx={{ color: ui.sub, fontSize: 13, mr: 'auto' }}>{formatMessage({ id: 'qiSaveFirst' })}</Box>
                        )}
                        <Button variant="outlined" onClick={() => { abort(); setBusy(null); setQs([]); setEditKey(null); setStep('upload') }}>
                            {formatMessage({ id: 'qiBack' })}
                        </Button>
                        <Button variant="contained" disabled={!canSave} onClick={save}>
                            {saving ? <CircularProgress size={20} /> : formatMessage({ id: 'qiImport' }, { count: qs.length })}
                        </Button>
                    </>
                )}
                {step === 'done' && (
                    <>
                        <Button variant="outlined" onClick={reset}>{formatMessage({ id: 'qiMore' })}</Button>
                        <Button variant="contained" onClick={close}>{formatMessage({ id: 'qiClose' })}</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ImportQuestionsDialog
